import { useQuery } from '@tanstack/react-query'
import { useApp } from '@/app/AppContext'
import { fetchApprovalHistory } from '@/approvals/api'
import type { ApprovalRequest, TimelineEvent } from '@/approvals/types'
import { ApiError } from '@/lib/api'
import type { CardTab, CardTabContext } from './types'
import { TimelineMore, useTimeline } from './useTimeline'
import { CallBody, inboundBody, inboundSender } from './recordHelpers'

/**
 * Вкладки «Лента» и «Согласования» (тикет #79, ТЗ §4.2 и §4.3).
 *
 * ## Почему лента — своя вкладка, а не часть истории стадий
 *
 * История стадий (#71) отвечает «как сделка шла по воронке». Лента отвечает «что вообще
 * происходило»: звонок, письмо, комментарий и смена стадии в одном потоке по времени.
 * Чтобы понять, почему сделка встала, надо увидеть, что звонок был **до** возврата на
 * переговоры, а комментарий — **после**; по двум спискам с разными форматами времени это
 * восстанавливают глазами.
 *
 * ## Почему согласования — отдельно от ленты
 *
 * Лента — это то, что случилось. Согласование — то, что **не** случилось и кого-то ждёт;
 * оно ещё и живёт своим маршрутом с шагами и комментариями решений. Строкой в
 * хронологии оно потеряло бы и статус, и то, на ком остановилось.
 */

/** Три состояния запроса одним компонентом. */
function AsyncSection<T>({
  query,
  empty,
  children,
}: {
  query: { isLoading: boolean; error: unknown; data: T | undefined }
  empty: string
  children: (data: T) => React.ReactNode
}) {
  const { t } = useApp()

  if (query.isLoading) return <p>{t('app.loading')}</p>
  if (query.error) {
    return (
      <p role="alert">
        {query.error instanceof ApiError && query.error.isForbidden
          ? t('access.denied')
          : t('app.error')}
      </p>
    )
  }
  if (!query.data) return <p className="muted">{empty}</p>
  return <>{children(query.data)}</>
}

// ------------------------------------------------------------------ лента

/**
 * Короткое имя вида события.
 *
 * Отдельный ключ на вид, а не «тип: call»: пользователь читает «Звонок», и подставлять
 * ему код из базы значит перекладывать перевод на него.
 */
function EventRow({ event, locale }: { event: TimelineEvent; locale: string }) {
  const { t } = useApp()

  return (
    <li className={`timeline-event timeline-${event.kind}`}>
      <span className="timeline-when">{formatMoment(event.at, locale)}</span>
      <span className="badge">{t(`timeline.kind.${event.kind}`)}</span>
      <span className="timeline-what">
        {event.kind === 'stage' ? (
          <>
            {String(event.details.fromStage || t('timeline.stage.created'))} →{' '}
            {String(event.details.toStage ?? '')}
            {/* Возврат назад помечается словом: цветовой признак не переживает ни
                печать, ни дальтонизм, а возврат — это и есть находка ленты. */}
            {event.details.backwards === true && (
              <span className="badge"> {t('timeline.stage.backwards')}</span>
            )}
          </>
        ) : (
          <>
            {event.subject ?? ''}
            {event.body && <span className="muted"> {truncate(event.body)}</span>}
            {/* Received letter (#121): marked inbound, with its sender, reply mark and files. */}
            {event.kind === 'email' && event.details.direction === 'inbound' ? (
              <>
                <span className="badge"> {t('mail.feed.inbound')}</span>
                <span className="muted"> {inboundSender(event)}</span>
                {inboundBody({ ...event, body: null }, t) && <span className="muted"> {inboundBody({ ...event, body: null }, t)}</span>}
              </>
            ) : null}
            {/* Call (#122): direction, length and the recording player. */}
            {event.kind === 'call' ? <CallBody event={{ ...event, body: null }} t={t} /> : null}
            {/* Messenger message (#125): channel, who wrote, and for our answer its delivery in words. */}
            {event.kind === 'message' ? <MessageMarks event={event} /> : null}
            {/* Outbound letter (#117): its delivery state in words — sent, retrying, refused. */}
            {event.kind === 'email' && event.details.delivery ? (
              <span className="badge"> {t(`mail.feed.${String(event.details.delivery)}`)}</span>
            ) : null}
          </>
        )}
      </span>
    </li>
  )
}

/**
 * Метки сообщения мессенджера: канал, направление, у входящего — кто написал, у нашего ответа —
 * доставка словами. «Отправлено» на сообщении, которое никуда не ушло, хуже отсутствия метки:
 * менеджер ждал бы ответа на вопрос, которого клиент не видел.
 */
function MessageMarks({ event }: { event: TimelineEvent }) {
  const { t } = useApp()
  const channel = String(event.details.channel ?? '')
  const inbound = event.details.direction === 'inbound'
  const delivery = String(event.details.delivery ?? '')
  return (
    <>
      <span className="badge"> {t(`messenger.channel.${channel}`)}</span>
      <span className="badge"> {t(`messenger.direction.${inbound ? 'inbound' : 'outbound'}`)}</span>
      {inbound && event.details.from ? <span className="muted"> {String(event.details.from)}</span> : null}
      {!inbound && delivery ? <span className="badge"> {t(`messenger.delivery.${delivery}`)}</span> : null}
      {!inbound && delivery === 'rejected' && event.details.deliveryReason
        ? <span className="muted"> {String(event.details.deliveryReason)}</span>
        : null}
    </>
  )
}

function TimelinePanel({ entityType, recordId }: { entityType: string; recordId: string }) {
  const { t, locale } = useApp()
  const query = useTimeline(entityType, recordId)

  return (
    <AsyncSection query={query} empty={t('timeline.empty')}>
      {(timeline) =>
        timeline.events.length === 0 ? (
          <p className="muted">{t('timeline.empty')}</p>
        ) : (
          <div className="card-gap">
            <ul className="timeline">
              {timeline.events.map((event) => (
                <EventRow key={`${event.kind}-${event.id}`} event={event} locale={locale} />
              ))}
            </ul>
            {/* «Показать раньше», а не «всего N»: точный счётчик стоил бы полного подсчёта
                по всем источникам на каждое открытие карточки ради цифры, по которой
                ничего не решают. */}
            <TimelineMore feed={query} />
          </div>
        )
      }
    </AsyncSection>
  )
}

/**
 * Вкладка «Лента».
 *
 * Только чтение. Записать звонок или задачу можно эндпоинтами `/sales/activities/*`,
 * но формы для них на этой вкладке нет — названо вслух, чтобы отсутствие кнопки не
 * читалось как сломанная кнопка.
 */
export function timelineTab(entityType: string): CardTab {
  return {
    id: 'timeline',
    labelKey: 'card.tab.timeline',
    render: (context: CardTabContext) => (
      <TimelinePanel entityType={entityType} recordId={context.recordId} />
    ),
  }
}

// ------------------------------------------------------------------ согласования

function ApprovalHistory({ requests }: { requests: ApprovalRequest[] }) {
  const { t, locale } = useApp()

  if (requests.length === 0) {
    return <p className="muted">{t('approvals.history.empty')}</p>
  }

  return (
    <div className="card-gap">
      {requests.map((request) => (
        <section key={request.id} className="approval-history">
          <h4>
            {t(`approvals.status.${request.status}`)}
            <span className="muted"> {formatMoment(request.requestedAt, locale)}</span>
          </h4>
          <table className="card-table">
            <thead>
              <tr>
                <th>{t('approvals.step')}</th>
                <th>{t('approvals.approver')}</th>
                <th>{t('approvals.stepStatus')}</th>
                <th>{t('approvals.decidedAt')}</th>
                <th>{t('approvals.comment')}</th>
              </tr>
            </thead>
            <tbody>
              {request.steps.map((step) => (
                <tr key={step.id}>
                  <td>{step.stepOrder}</td>
                  {/* Имя, а не идентификатор: «кто согласовал» — вопрос о человеке. */}
                  <td>{step.approverName ?? '—'}</td>
                  <td>{t(`approvals.stepStatus.${step.status}`)}</td>
                  <td>{step.decidedAt ? formatMoment(step.decidedAt, locale) : '—'}</td>
                  {/* Комментарий решения — часть следа, а не подсказка интерфейса:
                      причина отказа это то, ради чего историю открывают. */}
                  <td>{step.comment ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
    </div>
  )
}

function ApprovalsPanel({ entityType, recordId }: { entityType: string; recordId: string }) {
  const { t } = useApp()
  const query = useQuery({
    queryKey: ['approval-history', entityType, recordId],
    queryFn: ({ signal }) => fetchApprovalHistory(entityType, recordId, signal),
    retry: false,
  })

  return (
    <AsyncSection query={query} empty={t('approvals.history.empty')}>
      {(requests) => <ApprovalHistory requests={requests} />}
    </AsyncSection>
  )
}

/**
 * Вкладка «Согласования» — след решений на карточке документа.
 *
 * `entityType` здесь — тот, под которым согласования заведены на сервере (`quote`,
 * `invoice`), а не имя сущности в каталоге списков: маршрут открывается по первому, и
 * второе не нашло бы ничего.
 */
export function approvalsTab(entityType: string): CardTab {
  return {
    id: 'approvals',
    labelKey: 'card.tab.approvals',
    render: (context: CardTabContext) => (
      <ApprovalsPanel entityType={entityType} recordId={context.recordId} />
    ),
  }
}

function truncate(text: string): string {
  const flat = text.replace(/\s+/g, ' ').trim()
  return flat.length <= 120 ? flat : `${flat.slice(0, 117)}…`
}

function formatMoment(value: string, locale: string): string {
  const moment = new Date(value)
  if (Number.isNaN(moment.getTime())) return value
  return moment.toLocaleString(locale === 'en' ? 'en-GB' : 'ru-RU')
}
